import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Alert, 
  AlertTitle, 
  Button, 
  Box, 
  Typography, 
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import { 
  Warning as WarningIcon,
  Cancel as CancelIcon,
  MoneyOff as MoneyOffIcon,
  Gavel as GavelIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import adminPaymentAuthService from '../../services/adminPaymentAuthService';
import LoadingOverlay from '../common/LoadingOverlay';
import { getCurrencySymbol } from '../../utils/currency';

// Formatage universel des dates: DD/MM/YYYY HH:MM
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/**
 * Banner d'alerte pour notifier le renter qu'il doit valider un paiement 3DS
 * suite à une décision administrative
 * 
 * Ce composant est complètement isolé du flux de résolution amiable
 */
function AdminPaymentAuthBanner({ authRequest, onValidateClick, onRefresh, disputeStatus }) {
  const { t } = useTranslation('disputes');
  const { enqueueSnackbar } = useSnackbar();
  const [cancelling, setCancelling] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [sending, setSending] = useState(false);

  if (!authRequest) {
    return null;
  }

  // Symbole de devise
  const currencySymbol = getCurrencySymbol(authRequest.currency || 'EUR');

  const status = authRequest.status;

  const getBannerConfig = () => {
    switch (status) {
      case 'pending_auth':
        return {
          severity: 'warning',
          title: t('adminPaymentAuth.banners.pendingAuth.title'),
          subtitle: t('adminPaymentAuth.banners.pendingAuth.subtitle'),
          showActions: true
        };
      case 'authenticated':
        return {
          severity: 'info',
          title: t('adminPaymentAuth.banners.authenticated.title'),
          subtitle: t('adminPaymentAuth.banners.authenticated.subtitle'),
          showActions: false
        };
      case 'completed':
        return {
          severity: 'success',
          title: t('adminPaymentAuth.banners.completed.title'),
          subtitle: t('adminPaymentAuth.banners.completed.subtitle'),
          showActions: false
        };
      case 'cancelled':
        return {
          severity: 'warning',
          title: t('adminPaymentAuth.banners.cancelled.title'),
          subtitle: t('adminPaymentAuth.banners.cancelled.subtitle'),
          showActions: false
        };
      case 'expired':
        return {
          severity: 'warning',
          title: t('adminPaymentAuth.banners.expired.title'),
          subtitle: t('adminPaymentAuth.banners.expired.subtitle'),
          showActions: false
        };
      case 'failed':
        return {
          severity: 'error',
          title: t('adminPaymentAuth.banners.failed.title'),
          subtitle: authRequest.failure_reason ? t('adminPaymentAuth.banners.failed.subtitleWithReason', { reason: authRequest.failure_reason }) : t('adminPaymentAuth.banners.failed.subtitle'),
          showActions: false
        };
      default:
        return {
          severity: 'info',
          title: t('adminPaymentAuth.banners.default.title'),
          subtitle: t('adminPaymentAuth.banners.default.subtitle'),
          showActions: false
        };
    }
  };

  const banner = getBannerConfig();

  const borderColor =
    banner.severity === 'success' ? 'success.main' :
    banner.severity === 'error' ? 'error.main' :
    banner.severity === 'warning' ? 'warning.main' :
    'info.main';

  const handleConfirmRefuse = async () => {
    setConfirmDialogOpen(false);
    setCancelling(true);
    setSending(true);
    
    try {
      await adminPaymentAuthService.cancel(
        authRequest.id,
        t('adminPaymentAuth.messages.refusedBy')
      );
      
      enqueueSnackbar(
        t('adminPaymentAuth.messages.refusedSuccess'),
        { 
          variant: 'warning',
          autoHideDuration: 3000
        }
      );
      
      // Attendre puis FORCER LE RECHARGEMENT
      await new Promise(resolve => setTimeout(resolve, 1000));
      window.location.reload();
      
    } catch (error) {
      console.error('Erreur lors du refus:', error);
      enqueueSnackbar(
        `Erreur: ${error.message}`,
        { variant: 'error' }
      );
      setCancelling(false);
      setSending(false);
    }
  };

  return (
    <>
      <LoadingOverlay
        isOpen={sending}
        title={t('adminPaymentAuth.refusingTitle', 'Refus en cours...')}
        subtitle={t('adminPaymentAuth.refusingSubtitle', 'Nous traitons votre refus. Veuillez patienter...')}
        warning={t('adminPaymentAuth.doNotClose', 'Ne fermez pas cette page.')}
        primaryColor="#d32f2f"
        accentColor="#ff6f00"
      />
      <Alert
        severity={banner.severity}
        icon={<WarningIcon fontSize="large" />}
        sx={{
          mb: 3,
          border: '2px solid',
          borderColor,
          '& .MuiAlert-message': {
            width: '100%'
          }
        }}
        action={
          banner.showActions ? (
            <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
              <Button
                variant="outlined"
                color="error"
                size="medium"
                onClick={() => setConfirmDialogOpen(true)}
                disabled={cancelling}
                sx={{
                  minWidth: 120,
                  whiteSpace: 'nowrap'
                }}
              >
                {cancelling ? <CircularProgress size={20} /> : t('adminPaymentAuth.actions.refuse')}
              </Button>
              <Button
                variant="contained"
                color="warning"
                size="medium"
                onClick={onValidateClick}
                disabled={cancelling}
                sx={{
                  fontWeight: 'bold',
                  minWidth: 180,
                  whiteSpace: 'nowrap'
                }}
              >
                {cancelling ? <CircularProgress size={20} /> : t('adminPaymentAuth.actions.validate')}
              </Button>
            </Box>
          ) : null
        }
      >
      <AlertTitle sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
        {banner.title}
      </AlertTitle>
      
      <Box sx={{ mt: 1 }}>
        <Typography variant="body1" gutterBottom>
          {banner.subtitle}
        </Typography>

        <Typography variant="body1" gutterBottom>
          {t('adminPaymentAuth.labels.amount')}{' '}
          <strong style={{ fontSize: '1.2rem', color: 'error.main' }}>
            {authRequest.amount} {currencySymbol}
          </strong>
        </Typography>
        
        {authRequest.admin_message && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: 'background.paper',
              borderRadius: 1,
              borderLeft: '4px solid',
              borderColor
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              <strong>{t('adminPaymentAuth.labels.adminMessage')}</strong>
              <br />
              {authRequest.admin_message}
            </Typography>
          </Box>
        )}
        
        {authRequest.expires_at && disputeStatus !== 'resolved' && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {t('adminPaymentAuth.labels.expiresAt')}{' '}
            {formatDate(authRequest.expires_at)}
          </Typography>
        )}
      </Box>
    </Alert>

    {/* Dialog de confirmation moderne */}
    <Dialog
      open={confirmDialogOpen}
      onClose={() => setConfirmDialogOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderTop: '4px solid',
          borderColor: 'error.main'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <CancelIcon color="error" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h6" fontWeight="bold">
            {t('adminPaymentAuth.confirmDialog.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('adminPaymentAuth.confirmDialog.subtitle')}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>{t('adminPaymentAuth.labels.amount')}</AlertTitle>
          <Typography variant="h5" fontWeight="bold" color="error.main">
            {authRequest.amount} {currencySymbol}
          </Typography>
        </Alert>

        <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
          {t('adminPaymentAuth.confirmDialog.description')}
        </Typography>

        <List dense>
          <ListItem>
            <ListItemIcon>
              <GavelIcon color="error" />
            </ListItemIcon>
            <ListItemText
              primary={t('adminPaymentAuth.confirmDialog.reasons.adminDecision')}
              secondary={t('adminPaymentAuth.confirmDialog.reasons.adminDecisionDesc')}
              primaryTypographyProps={{ fontWeight: 'medium' }}
            />
          </ListItem>

          <ListItem>
            <ListItemIcon>
              <MoneyOffIcon color="error" />
            </ListItemIcon>
            <ListItemText
              primary={t('adminPaymentAuth.confirmDialog.reasons.financial')}
              secondary={t('adminPaymentAuth.confirmDialog.reasons.financialDesc')}
              primaryTypographyProps={{ fontWeight: 'medium' }}
            />
          </ListItem>

          <ListItem>
            <ListItemIcon>
              <InfoIcon color="error" />
            </ListItemIcon>
            <ListItemText
              primary={t('adminPaymentAuth.confirmDialog.reasons.escalation')}
              secondary={t('adminPaymentAuth.confirmDialog.reasons.escalationDesc')}
              primaryTypographyProps={{ fontWeight: 'medium' }}
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 2 }} />

        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {t('adminPaymentAuth.confirmDialog.alert')}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={() => setConfirmDialogOpen(false)}
          color="inherit"
          variant="outlined"
        >
          {t('adminPaymentAuth.confirmDialog.cancel')}
        </Button>
        <Button
          onClick={handleConfirmRefuse}
          color="error"
          variant="contained"
          startIcon={<CancelIcon />}
          sx={{ fontWeight: 'bold' }}
        >
          {t('adminPaymentAuth.confirmDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  </>
  );
}

export default AdminPaymentAuthBanner;
